// SALT2 Podcast（YouTube「SALT2」チャンネル @salt2_official の本編）。
// 回の一覧は podcast.json。scripts/update-podcast.mjs が週1回チャンネルから新しい回を足す
// （.github/workflows/update-podcast.yml。2026-10-18 代表指示）。
// 載せるのは題名に「#n」の番号がある回だけ（ショート動画と、番号の無い講演などは載せない）。
// title は YouTube の題名から「#n」を外したもの。手で直した題名（読点など）は、自動の取り込みでは上書きしない。
// description・published・seconds は各動画のページから取ったもの。回ごとのページ（/news/podcast/<番号>）で使う
// （検索と AI の回答に、動画の中身を文章で読んでもらうため。2026-10-18）。
import data from './podcast.json';

export const PODCAST_CHANNEL_URL = data.channelUrl;

// 新しい回から順に。no は表示用の「#9」、num は数（URL に使う）
export const podcastEpisodes = [...data.episodes]
  .filter((e) => Number.isInteger(e.no))
  .sort((a, b) => b.no - a.no)
  .map((e) => ({ ...e, num: e.no, no: `#${e.no}`, path: `/news/podcast/${e.no}` }));

export const findEpisode = (num) => podcastEpisodes.find((e) => String(e.num) === String(num));

// 公開日を日本時間の「2026.10.18」にする
export function formatEpisodeDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const parts = new Intl.DateTimeFormat('ja-JP', {
    timeZone: 'Asia/Tokyo',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(d);
  const get = (type) => parts.find((p) => p.type === type)?.value;
  return `${get('year')}.${get('month')}.${get('day')}`;
}

// 長さ（秒）を構造化データの形（PT28M32S）にする
export function isoDuration(seconds) {
  const s = Math.round(Number(seconds) || 0);
  if (s <= 0) return undefined;
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const r = s % 60;
  return `PT${h ? `${h}H` : ''}${m ? `${m}M` : ''}${r ? `${r}S` : ''}` || undefined;
}

const CREDIT = /creative\s*commons|incompetech|creativecommons\.org/i;

/**
 * YouTube の説明文を、ページに出す形に分ける。
 * - lead: 最初の「▼」より前の段落（あらすじ）
 * - sections: 「▼エピソード詳細」「▼出演者」などの見出しごとの中身。「・」で始まる行は items、ほかは paragraphs
 * - credits: 使った楽曲の表記（Creative Commons など）。小さく出す
 * 「#SALT2 #…」のようなハッシュタグだけの行は出さない。
 */
export function parseDescription(text = '') {
  const lines = text.replace(/\r\n/g, '\n').split('\n');
  const lead = [];
  const sections = [];
  const credits = [];
  let current = null;
  let para = [];
  const flush = () => {
    if (!para.length) return;
    const p = para.join('\n');
    para = [];
    if (CREDIT.test(p)) credits.push(p);
    else if (current) current.paragraphs.push(p);
    else lead.push(p);
  };
  for (const raw of lines) {
    const line = raw.trim();
    if (!line) {
      flush();
      continue;
    }
    if (/^(#\S+\s*)+$/.test(line)) {
      flush();
      continue;
    }
    if (line.startsWith('▼')) {
      flush();
      current = { title: line.slice(1).trim(), items: [], paragraphs: [] };
      sections.push(current);
      continue;
    }
    if (current && /^[・•]/.test(line)) {
      flush();
      current.items.push(line.replace(/^[・•]\s*/, ''));
      continue;
    }
    // 続く行は1つの段落にまとめる（楽曲の表記も、曲名・作者・ライセンスの行が1つの段落になる）
    para.push(line);
  }
  flush();
  return { lead, sections: sections.filter((s) => s.items.length || s.paragraphs.length), credits };
}

/**
 * ページの説明文と動画の構造化データに使う、その回の要約（1行）。
 * ふつうはあらすじ（lead）。説明文が「▼ブートキャンプのリンク」のような見出しで始まってあらすじが無い回（#5・#6）は、
 * 見出しの下の最初の本文の段落を使う（URL だけの段落と、「「SALT2ラジオ」第5回」のような短い頭の行は外す）。
 */
export function episodeSummary(parsed, title = '') {
  const oneLine = (s) => s.replace(/\s+/g, ' ').trim();
  if (parsed.lead.length) return oneLine(parsed.lead.join(' '));
  for (const section of parsed.sections) {
    for (const p of section.paragraphs) {
      const lines = p.split('\n').filter((l) => !/^https?:\/\//.test(l.trim()));
      if (lines.length > 1 && lines[0].length < 20) lines.shift();
      const text = oneLine(lines.join(' '));
      if (text.length >= 40) return text;
    }
  }
  return oneLine(title);
}
